<template>
  <div class="my-jobs-list">
    <div class="my-jobs-filters">
      <b-form-input
        v-model="searchQuery"
        placeholder="Search by title, site or address"
        class="my-jobs-search"
        @update="onFilterChange"
      />
      <b-form-select
        v-model="statusFilter"
        :options="statusOptions"
        class="my-jobs-status-filter"
        @change="onFilterChange"
      />
    </div>

    <b-overlay :show="loading" rounded="sm">
      <div v-if="!loading && jobs.length === 0" class="my-jobs-empty">
        No scheduled jobs found.
      </div>

      <b-card
        v-for="job in jobs"
        :key="job.id"
        class="my-job-card"
        border-variant="light"
      >
        <div class="my-job-card-header">
          <h6 class="my-job-title">{{ job.title }}</h6>
          <b-badge :variant="statusVariant(job.status)">{{
            statusLabel(job.status)
          }}</b-badge>
        </div>

        <div class="my-job-detail">
          <b-icon-calendar class="mr-1" />
          {{ formatDate(job.date) }}
          <span v-if="job.start_time || job.end_time">
            &middot; {{ job.start_time || '?' }} - {{ job.end_time || '?' }}
          </span>
        </div>

        <div v-if="job.site_name || job.address" class="my-job-detail">
          <b-icon-geo-alt class="mr-1" />
          <span v-if="job.site_name">{{ job.site_name }}</span>
          <span v-if="job.site_name && job.address"> &middot; </span>
          <span v-if="job.address">{{ job.address }}</span>
        </div>

        <div v-if="job.project_name" class="my-job-detail">
          <b-icon-briefcase class="mr-1" />
          {{ job.project_name }}
        </div>

        <div v-if="primarySupervisor(job)" class="my-job-detail">
          <b-icon-person class="mr-1" />
          Supervisor: {{ primarySupervisor(job).name
          }}<span v-if="primarySupervisor(job).phone">
            &middot; {{ primarySupervisor(job).phone }}</span
          >
        </div>

        <p v-if="job.instructions" class="my-job-instructions">
          {{ job.instructions }}
        </p>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'MyJobsList',

  data() {
    return {
      loading: false,
      searchQuery: '',
      statusFilter: null,
      statusOptions: [
        { value: null, text: 'All statuses' },
        { value: 'upcoming', text: 'Upcoming' },
        { value: 'completed', text: 'Completed' },
        { value: 'cancelled', text: 'Cancelled' },
      ],
    }
  },

  computed: {
    ...mapGetters('schedule', ['myJobs']),

    jobs() {
      return this.myJobs
    },
  },

  created() {
    this.fetchMyJobs()
  },

  methods: {
    ...mapActions('schedule', { getMyJobs: 'getMyJobs' }),

    async fetchMyJobs() {
      this.loading = true
      try {
        await this.getMyJobs({
          search: this.searchQuery || undefined,
          status: this.statusFilter || undefined,
        })
      } catch (e) {
        this.$bvToast.toast('Failed to load your scheduled jobs.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.loading = false
      }
    },

    onFilterChange() {
      this.fetchMyJobs()
    },

    formatDate(date) {
      return date ? dayjs(date).format('ddd, D MMM YYYY') : ''
    },

    primarySupervisor(job) {
      return (job.supervisors && job.supervisors[0]) || null
    },

    statusVariant(status) {
      if (status === 'completed') return 'success'
      if (status === 'cancelled') return 'secondary'
      return 'primary'
    },

    statusLabel(status) {
      if (!status) return ''
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
  },
}
</script>

<style scoped>
.my-jobs-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.my-jobs-search {
  flex: 1;
  min-width: 200px;
}

.my-jobs-status-filter {
  max-width: 200px;
}

.my-jobs-empty {
  text-align: center;
  padding: 40px 0;
  color: #888;
}

.my-job-card {
  margin-bottom: 12px;
}

.my-job-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.my-job-title {
  margin: 0;
}

.my-job-detail {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 4px;
}

.my-job-instructions {
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 0.9rem;
  color: #555;
}
</style>
